/**
 * Préchargement des ressources nécessaires au dessin sur canvas.
 *
 * Deux pièges Lectio évités ici :
 *  - Les polices Google (display=swap) se chargent en asynchrone. Si on dessine
 *    avant, le canvas retombe silencieusement sur une police système. On attend
 *    donc explicitement Instrument Serif et Geist.
 *  - Un composant React en `currentColor` ne se dessine pas sur un canvas. On
 *    part du vrai fichier SVG, on le repeint en blanc (pour ressortir sur fond
 *    sombre) et on le recadre au contenu par balayage alpha, pour un placement
 *    précis dans le coin, quelles que soient les marges transparentes du fichier.
 */

import brandLogoRaw from '@/assets/brand-logo.svg?raw';
import { buildOptimizedImageUrl } from '@/lib/images';

const FONT_FACES = [
  '400 64px "Instrument Serif"',
  'italic 64px "Instrument Serif"',
  '400 32px "Geist"',
  '500 32px "Geist"',
  '600 32px "Geist"',
  '500 28px "Geist Mono"',
];

/** Attend que les polices de l'image soient réellement chargées avant de dessiner. */
export async function ensureShareFontsLoaded(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    await Promise.all(FONT_FACES.map((face) => document.fonts.load(face)));
    await document.fonts.ready;
  } catch {
    /* on dessine quand même : au pire la police de secours */
  }
}

export interface ShareLogo {
  /** Source prête pour `drawImage`, en blanc et recadrée au contenu. */
  source: CanvasImageSource;
  width: number;
  height: number;
}

const LOGO_RENDER_HEIGHT = 512;
const ALPHA_THRESHOLD = 8;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('logo introuvable'));
    img.src = src;
  });
}

function cropToContent(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): ShareLogo {
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return { source: canvas, width, height };

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const cropped = document.createElement('canvas');
  cropped.width = cropW;
  cropped.height = cropH;
  cropped.getContext('2d')?.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
  return { source: cropped, width: cropW, height: cropH };
}

/**
 * Charge la photo de l'événement pour l'utiliser en fond. Passe par le proxy
 * d'images (wsrv.nl, qui renvoie les en-têtes CORS) et `crossOrigin`, sinon le
 * canvas serait « taint » et `toBlob` échouerait. Rejette si l'image est
 * inaccessible → l'appelant retombe alors sur un dégradé.
 */
export function loadShareBackgroundImage(rawUrl: string): Promise<HTMLImageElement> {
  const url = buildOptimizedImageUrl(rawUrl, 1080, 82);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('photo événement inaccessible'));
    img.src = url;
  });
}

let logoPromise: Promise<ShareLogo> | null = null;

/** Charge le logo LaFête en blanc, recadré au contenu (résultat mémoïsé). */
export function loadShareLogo(): Promise<ShareLogo> {
  if (logoPromise) return logoPromise;
  logoPromise = (async () => {
    const whiteSvg = brandLogoRaw.replace(/fill:#000000/gi, 'fill:#FFFFFF');
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(whiteSvg)}`;
    const img = await loadImage(svgUrl);
    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
    const height = LOGO_RENDER_HEIGHT;
    const width = Math.round(height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { source: canvas, width, height };
    ctx.drawImage(img, 0, 0, width, height);
    return cropToContent(canvas, ctx);
  })();
  return logoPromise;
}
